import React, {
  createContext,
  useState,
  useCallback,
  useContext,
  ReactNode,
  useEffect,
} from 'react';
import { startSessionGuard } from '../utils/sessionGuard';
import { CENTRAL_AUTH_ORIGIN } from '../utils/sso';

const AUTH_COOKIE_SUFFIX = import.meta.env.VITE_AUTH_COOKIE_SUFFIX || '';
const AUTH_USER_COOKIE = `spm_user${AUTH_COOKIE_SUFFIX}`;
const AUTH_TOKEN_COOKIE = `spm_token${AUTH_COOKIE_SUFFIX}`;
// Namespaced like the auth cookies. It is set on the shared .surbhi.net parent
// domain, so without the env suffix a logout on demo would force prompt:'login'
// on prod (and vice versa) — the cross-environment SSO re-prompt bug.
const JUST_LOGGED_OUT_COOKIE = `spm_just_logged_out${AUTH_COOKIE_SUFFIX}`;
const SSO_INTROSPECT_URL = `${CENTRAL_AUTH_ORIGIN}/api/auth/sso/introspect`;

function clearCookie(name: string) {
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${name}=; Path=/; Max-Age=0; SameSite=Lax${secure}`;
  document.cookie = `${name}=; Path=/; Domain=.surbhi.net; Max-Age=0; SameSite=Lax${secure}`;
}

function clearAuthState() {
  localStorage.removeItem('user');
  localStorage.removeItem('token');
  clearCookie(AUTH_USER_COOKIE);
  clearCookie(AUTH_TOKEN_COOKIE);
}

function getCookie(name: string): string | null {
  const cookie = document.cookie.split('; ').find((row) => row.startsWith(`${name}=`));
  if (!cookie) return null;
  return decodeURIComponent(cookie.split('=').slice(1).join('='));
}

function isJwtExpired(token: string): boolean {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return true;
    const payload = JSON.parse(atob(parts[1]));
    const exp = Number(payload?.exp || 0);
    if (!exp) return false;
    return Date.now() >= exp * 1000;
  } catch {
    return true;
  }
}

function bootstrapUser(): User | null {
  const cookieUser = getCookie(AUTH_USER_COOKIE);
  const cookieToken = getCookie(AUTH_TOKEN_COOKIE);
  if (!cookieUser || !cookieToken) return null;
  if (isJwtExpired(cookieToken)) return null;
  try {
    const user = JSON.parse(cookieUser) as User;
    localStorage.setItem('user', JSON.stringify(user));
    localStorage.setItem('token', cookieToken);
    return user;
  } catch {
    return null;
  }
}

export interface UserPermissions {
  // These arrive from the central `spm_user` SSO cookie, which serializes MySQL
  // tinyint columns — so every flag is a NUMBER (1/0) in practice, not a boolean.
  // Never test them with `=== true`; use isAdminFlag() from utils/helpers.
  can_view_products: boolean | number;
  can_view_tasks: boolean | number;
  can_view_emd: boolean | number;
  can_view_insurance?: boolean | number;
  can_view_letters?: boolean | number;
  can_create_letters?: boolean | number;
  can_delete_letters?: boolean | number;
  is_authorized_signatory?: boolean | number;
  is_admin: boolean | number;
  [key: string]: boolean | number | undefined;
}

export interface User {
  id: string;
  email: string;
  name: string;
  permissions?: UserPermissions;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (user: User, token: string) => void;
  logout: () => void;
  setUser: (user: User | null) => void;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(() => bootstrapUser());
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const verify = async () => {
      const token = getCookie(AUTH_TOKEN_COOKIE);
      if (!token || isJwtExpired(token)) {
        clearAuthState();
        if (!cancelled) {
          setUser(null);
          setIsLoading(false);
        }
        return;
      }
      try {
        const res = await fetch(SSO_INTROSPECT_URL, {
          credentials: 'include',
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || data?.active !== true) {
          clearAuthState();
          if (!cancelled) {
            setUser(null);
            setIsLoading(false);
          }
          return;
        }
        // Session is valid. Ensure user state is set — bootstrapUser() may have
        // returned null if the spm_user cookie was absent (e.g. cleared by browser)
        // while spm_token was still present. In that case, build the user from
        // the introspect payload so the app doesn't stay stuck on "Login Required".
        if (!cancelled) {
          const fromCookie = bootstrapUser();
          if (!fromCookie && data?.user) {
            const introspectUser: User = {
              id: String(data.user.id || ''),
              email: String(data.user.email || ''),
              name: String(data.user.name || data.user.email || ''),
              // Introspect returns live permissions. Carrying them over matters:
              // without them every module gate saw `permissions: undefined`.
              ...(data.user.permissions ? { permissions: data.user.permissions } : {}),
            };
            setUser(introspectUser);
            localStorage.setItem('user', JSON.stringify(introspectUser));
          } else if (fromCookie && data?.user?.permissions) {
            // Cookie present but possibly stale (permissions are only rewritten on
            // login/refresh). Introspect is authoritative and live, so prefer it.
            const merged: User = { ...fromCookie, permissions: data.user.permissions };
            setUser(merged);
            localStorage.setItem('user', JSON.stringify(merged));
          }
        }
      } catch {
        // Network error - trust cookie
        if (!cancelled) {
          const u = bootstrapUser();
          setUser(u);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    verify();
    return () => {
      cancelled = true;
    };
  }, []);

  // Slide the session while active; enforce a short idle timeout.
  useEffect(() => {
    const idleMin = Number((import.meta.env.VITE_IDLE_TIMEOUT_MIN as string) || '30');
    startSessionGuard({
      centralOrigin: CENTRAL_AUTH_ORIGIN,
      idleMs: (Number.isFinite(idleMin) && idleMin > 0 ? idleMin : 30) * 60 * 1000,
    });
  }, []);

  // Pick up permission changes without a manual reload.
  //
  // The session guard silently refreshes the shared session every few minutes, and
  // the central issuer rewrites `spm_user` with the CURRENT permissions each time.
  // Without this effect the React state kept whatever was read at mount, so a user
  // whose Letters access was just granted in the Admin Console saw a stale UI (menus
  // missing, or an "Access denied" gate) until they hard-reloaded the portal.
  useEffect(() => {
    // Order-insensitive comparison: the central issuer rewrites the cookie on
    // every session refresh and its key order / extra fields can differ from the
    // copy held in state. A plain string compare treated that as a change and
    // replaced `user` every minute, which re-ran every effect depending on it —
    // the letter editor silently reloaded the letter while it was being edited.
    const stable = (value: unknown): string =>
      JSON.stringify(value, (_key, v) =>
        v && typeof v === 'object' && !Array.isArray(v)
          ? Object.keys(v).sort().reduce<Record<string, unknown>>((acc, k) => { acc[k] = (v as Record<string, unknown>)[k]; return acc; }, {})
          : v,
      );
    const syncFromCookie = () => {
      const cookieUser = getCookie(AUTH_USER_COOKIE);
      if (!cookieUser) return;
      setUser((current) => {
        try {
          const next = JSON.parse(cookieUser) as User;
          if (!next?.id) return current;
          if (current && stable(current) === stable(next)) return current;
          localStorage.setItem('user', cookieUser);
          return next;
        } catch {
          return current;
        }
      });
    };

    const interval = window.setInterval(syncFromCookie, 60 * 1000);
    const onVisible = () => {
      if (!document.hidden) syncFromCookie();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  const login = useCallback((newUser: User, token: string) => {
    localStorage.setItem('user', JSON.stringify(newUser));
    localStorage.setItem('token', token);
    setUser(newUser);
  }, []);

  const logout = useCallback(() => {
    clearAuthState();
    // Clear MSAL cache so next login asks for credentials
    Object.keys(localStorage)
      .filter((k) => k.startsWith('msal.') || k.includes('msal') || k.includes('authority'))
      .forEach((k) => localStorage.removeItem(k));
    // Clear spm cookies
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `${AUTH_TOKEN_COOKIE}=; Path=/; Domain=.surbhi.net; Max-Age=0; SameSite=Lax${secure}`;
    document.cookie = `${AUTH_USER_COOKIE}=; Path=/; Domain=.surbhi.net; Max-Age=0; SameSite=Lax${secure}`;
    document.cookie = `${JUST_LOGGED_OUT_COOKIE}=1; Path=/; Domain=.surbhi.net; Max-Age=300; SameSite=Lax${secure}`;
    setUser(null);
    window.location.href = `${CENTRAL_AUTH_ORIGIN}/login?logged_out=1`;
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        login,
        logout,
        setUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
